import React, { useMemo } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { DateTime } from 'luxon';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import Checkbox from '@/components/General/inputs/Checkbox';
import useTableSelectionStore from '@/data/store/useTableSelectionStore';

const getLocation = person => [person.Address?.city, person.Address?.state].filter(Boolean).join(', ');

const getLastVisit = person => {
    if (!person.mostRecentVisit) return 'No visits yet';
    const date = DateTime.fromISO(person.mostRecentVisit);
    return date.isValid ? `Last visit ${date.toLocaleString(DateTime.DATE_MED)}` : 'No visits yet';
};

const DirectoryPersonCard = ({ person, tableId, selecting, canViewBasic, canViewContact, onOpen, onToggle, onLongPress }) => {
    const isSelected = useTableSelectionStore(state => selecting ? state.isSelected(tableId, person.id) : false);
    const lastVisit = useMemo(() => canViewBasic ? getLastVisit(person) : '', [canViewBasic, person]);
    const location = useMemo(() => canViewContact ? getLocation(person) : '', [canViewContact, person]);

    return (
        <View style={styles.row}>
            <Pressable
                testID={`PeopleDirectoryCard-${person.id}`}
                accessibilityRole="button"
                accessibilityLabel={`${person.firstName} ${person.lastName}`}
                onPress={selecting ? onToggle : onOpen}
                onLongPress={onLongPress}
                style={({ pressed }) => styles.card(isSelected, pressed)}
            >
                <Avatar size={54} profile={person} disableBorder />
                <View style={styles.content}>
                    <View style={styles.nameRow}>
                        <Text numberOfLines={1} style={styles.name}>{person.firstName} {person.lastName}</Text>
                        {person.isArchived && (
                            <View style={styles.archivedBadge}>
                                <Text style={styles.archivedText}>Archived</Text>
                            </View>
                        )}
                    </View>
                    {(canViewBasic || location) && (
                        <View style={styles.details}>
                            {canViewBasic && (
                                <View style={styles.detailRow}>
                                    <Unicon name="calendar-outline" size={14} colorVariant="textSecondary" />
                                    <Text numberOfLines={1} style={styles.detailText}>{lastVisit}</Text>
                                </View>
                            )}
                            {!!location && (
                                <View style={styles.detailRow}>
                                    <Unicon name="location-outline" size={14} colorVariant="textTertiary" />
                                    <Text selectable numberOfLines={1} style={styles.locationText}>{location}</Text>
                                </View>
                            )}
                        </View>
                    )}
                </View>
                {selecting ? (
                    <Checkbox
                        testID={`PeopleDirectoryCheckbox-${person.id}`}
                        checked={isSelected}
                        onChange={onToggle}
                        size={22}
                    />
                ) : (
                    <View style={styles.chevron}>
                        <Unicon name="chevron-forward" size={17} colorVariant="textTertiary" />
                    </View>
                )}
            </Pressable>
        </View>
    );
};

const styles = StyleSheet.create(theme => ({
    row: {
        paddingHorizontal: theme.spacing.md,
    },
    card: (selected, pressed) => ({
        minHeight: 88,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 13,
        paddingHorizontal: 14,
        paddingVertical: 14,
        borderRadius: theme.roundness.lg,
        borderCurve: 'continuous',
        backgroundColor: selected ? theme.colors.peopleContainer : theme.colors.surface,
        boxShadow: selected ? `inset 0 0 0 1px ${theme.colors.people}` : theme.shadows.card,
        opacity: pressed ? 0.76 : 1,
        transform: [{ scale: pressed ? 0.99 : 1 }],
        _web: {
            cursor: 'pointer',
            transition: 'transform 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
            _hover: {
                backgroundColor: selected ? theme.colors.peopleContainer : theme.colors.hoverCard,
                boxShadow: theme.shadows.cardHover,
            },
        },
    }),
    content: {
        flex: 1,
        minWidth: 0,
        gap: 7,
    },
    nameRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    name: {
        flexShrink: 1,
        color: theme.colors.text,
        fontFamily: theme.fonts.heading,
        fontSize: 16,
        letterSpacing: -0.15,
    },
    archivedBadge: {
        paddingHorizontal: 7,
        paddingVertical: 2,
        borderRadius: 999,
        backgroundColor: theme.colors.errorContainer,
    },
    archivedText: {
        color: theme.colors.error,
        fontFamily: theme.fonts.bold,
        fontSize: 10,
    },
    details: {
        gap: 3,
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        minWidth: 0,
    },
    detailText: {
        flexShrink: 1,
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.body,
        fontSize: 13,
    },
    locationText: {
        flexShrink: 1,
        color: theme.colors.textTertiary,
        fontFamily: theme.fonts.body,
        fontSize: 12,
    },
    chevron: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: theme.colors.surfaceVariant,
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

export default React.memo(DirectoryPersonCard);
